<template>
  <f7-page name="group-members" @page:init="load">
    <f7-navbar :title="`${$t('groupMembersTitle')} ${groupName ? ' - ' + groupName : ''}`" back-link>
      <f7-nav-right>
        <f7-link icon-f7="person_badge_plus" @click="inviteSheetOpen = true" />
      </f7-nav-right>
    </f7-navbar>

    <div class="members-page">
      <!-- Group Summary Banner Card -->
      <div class="summary-card">
        <div class="summary-head">
          <div class="summary-head-main">
            <img v-if="logoUrl(group)" :src="logoUrl(group)" :alt="group?.name" class="summary-logo" />
            <div v-else class="summary-icon">
              <span class="material-symbols-outlined" style="font-size: 22px">groups_3</span>
            </div>
            <div class="summary-titles">
              <h2 class="summary-name">{{ group?.name || $t('communityCircle') }}</h2>
              <p v-if="group?.description" class="summary-subtitle" v-html="group?.description"></p>
            </div>
          </div>
          <span v-if="groupStatus" class="summary-status-pill" :class="groupStatusClass">{{ groupStatus }}</span>
          <span v-else class="summary-status">{{ $t('statusActive') }}</span>
        </div>

        <div class="summary-stats">
          <div class="summary-stat">
            <div class="stat-icon stat-icon--primary">
              <f7-icon material="how_to_reg" :size="16" />
            </div>
            <div class="stat-body">
              <p class="stat-value">{{ $t('membersCount', { count: membersStore.members.length }) }}</p>
              <p class="summary-subtitle">{{ $t('activeMembers') }}</p>
            </div>
          </div>
          <div class="summary-stat">
            <div class="stat-icon stat-icon--secondary">
              <span class="material-symbols-outlined" style="font-size: 16px">shield_person</span>
            </div>
            <div class="stat-body">
              <p class="stat-value"> {{ $t('adminsCount', { count: admins.length }) }}</p>
              <p class="summary-subtitle">{{ $t('maxPrivileges') }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Button -->
      <f7-button
        v-if="viewerCanManage"
        large
        fill
        round
        raised
        @click="inviteSheetOpen = true"
      >
        <f7-icon material="person_add" :size="20" />
        <span>{{ $t('importOrInviteMembers') }}</span>
      </f7-button>

      <!-- Search & Filter Chips -->
      <div class="filters">
        <div class="search" style="border-radius: 25px;">
          <span class="search-icon">
            <f7-icon material="search" :size="20" />
          </span>
          <input
          style="border-radius: 25px;"
            v-model="searchQuery"
            type="text"
            :placeholder="$t('searchByName')"
            class="avocash-input search-input"
          />
        </div>

        <div class="filter-chips">
          <f7-link
            class="filter-chip filter-chip--all"
            :class="activeFilter === 'all' ? 'filter-chip--active' : 'filter-chip--idle'"
            @click="activeFilter = 'all'"
          >
            <span>{{ $t('filterAll') }}</span>
            <f7-badge class="filter-chip-count">{{ membersStore.members.length }}</f7-badge>
          </f7-link>
          <f7-link
            class="filter-chip"
            :class="activeFilter === 'admins' ? 'filter-chip--active' : 'filter-chip--idle'"
            @click="activeFilter = 'admins'"
          >
            <f7-icon material="security" :size="14" />
            <span>{{ $t('filterAdmins', { count: admins.length }) }}</span>
          </f7-link>
          <f7-link
            class="filter-chip"
            :class="activeFilter === 'regulars' ? 'filter-chip--active' : 'filter-chip--idle'"
            @click="activeFilter = 'regulars'"
          >
            <span>{{ $t('filterMembers', { count: regularMembers.length }) }}</span>
          </f7-link>
        </div>
      </div>

      <!-- Tabs Navigation -->
      <f7-toolbar tabbar>
        
          <f7-link tab-link="#tab-members" tab-link-active>
            {{ $t('membersTab', { count: filteredMembers.length }) }}
          </f7-link>
          <f7-link tab-link="#tab-invitations">
            {{ $t('invitationsTab', { count: pendingCount }) }}
          </f7-link>
        
      </f7-toolbar>

      <f7-tabs>
        <!-- MEMBERS TAB -->
        <f7-tab id="tab-members" tab-active>
          <div class="tab-panel">
            <div v-if="filteredAdmins.length">
              <h3 class="section-title">{{ $t('adminsSection') }}</h3>
              <div class="member-group">
                <MemberCard
                  v-for="m in filteredAdmins"
                  :key="m.userId"
                  :member="m"
                  :can-manage="viewerCanManage && m.role !== 'OWNER'"
                  :row-actionable="viewerCanManage && m.role !== 'OWNER'"
                  :show-nudge="false"
                  @open-menu="openMenu"
                  @open-actions="openMenu"
                />
              </div>
            </div>

            <div v-if="filteredRegulars.length">
              <h3 class="section-title">{{ $t('membersSection') }}</h3>
              <div class="member-group">
                <MemberCard
                  v-for="m in filteredRegulars"
                  :key="m.userId"
                  :member="m"
                  :can-manage="viewerCanManage"
                  :row-actionable="viewerCanManage"
                  :show-nudge="overdueUserIds.has(m.userId)"
                  @open-menu="openMenu"
                  @open-actions="openMenu"
                  @nudge="handleNudge"
                />
              </div>
            </div>

            <div v-if="guests.length && activeFilter === 'all'">
              <h3 class="section-title">{{ $t('guestsSection') }}</h3>
              <div class="member-group">
                <MemberCard
                  v-for="m in guests"
                  :key="m.userId"
                  :member="m"
                  :can-manage="viewerCanManage"
                  :row-actionable="viewerCanManage"
                  @open-menu="openMenu"
                  @open-actions="openMenu"
                />
              </div>
            </div>

            <div v-if="viewerCanManage" class="tab-footer">
              <f7-button large raised fill round @click="goImport">
                <f7-icon material="download" :size="20" color="white" />
                <span >{{ $t('importMembers') }}</span>
              </f7-button>
            </div>
          </div>
        </f7-tab>

        <!-- INVITATIONS TAB -->
        <f7-tab id="tab-invitations">
          <div class="tab-panel">
            <template v-if="membersStore.joinRequests.length">
              <h3 class="section-title">{{ $t('pendingInvitations') }}</h3>
              <div class="member-group">
                <PendingInvitationRow
                  v-for="inv in membersStore.joinRequests"
                  :key="inv.id"
                  :invitation="inv"
                  mode="requested"
                  @accept="handleAccept"
                  @decline="handleDecline"
                />
              </div>
            </template>

            <h3 class="section-title">{{ $t('invitationsSent') }}</h3>
            <div class="member-group">
              <PendingInvitationRow
                v-for="inv in membersStore.sentInvitations"
                :key="inv.id"
                :invitation="inv"
                mode="sent"
                @resend="handleResend"
              />
              <div v-if="!membersStore.sentInvitations.length" class="empty-invites">
                {{ $t('noPendingInvitations') }}
              </div>
            </div>

            <f7-button large fill raised round class="invite-btn" @click="inviteSheetOpen = true">
              {{ $t('inviteNewMember') }}
            </f7-button>
          </div>
        </f7-tab>
      </f7-tabs>
    </div>

    <!-- Role change / remove action sheet -->
    <RoleChangeSheet
      ref="roleChangeSheetRef"
      @change-role="handleRoleChange"
      @remove="handleRemove"
    />

    <!-- Invite new member sheet -->
    <AdaptiveSheet
      v-model:opened="inviteSheetOpen"
      swipe-to-close
      close-on-escape
      :tablet-fullscreen="false"
      
    >
      <f7-page-content>
        <f7-navbar :title="$t('inviteMemberTitle')">
          <f7-nav-right>
            <f7-link sheet-close popup-close><f7-icon ios="f7:multiply" md="material:close" /></f7-link>
          </f7-nav-right>
        </f7-navbar>
        <f7-block strong inset>
          <f7-segmented  round class=" margin-bottom">
            <f7-button round raised  :fill="channel === 'WHATSAPP'" @click="selectInviteChannnel('WHATSAPP')">
              {{ $t('whatsapp') }}
            </f7-button>
            <f7-button  style="border-radius:0px!important" raised  :fill="channel==='SMS'" @click="selectInviteChannnel('SMS')">
              {{ $t('sms') }}
            </f7-button>
            <f7-button  round raised  :fill="channel === 'EMAIL'" @click="selectInviteChannnel('EMAIL')">
              {{ $t('email') }}
            </f7-button>
          </f7-segmented>
          <input
           :value="inviteTarget"
            :label="channel==='EMAIL'?$t('email'):$t('common.phoneNumber')"
            :type="channel === 'EMAIL' ? 'email' : 'tel'"
            required
            name="inviteTarget"
            :placeholder="channel==='EMAIL'?$t('email'):$t('common.phoneNumber')"
            class="avocash-input"
          />
           <f7-button fill round raised large class="margin-top" @click="sendInvite()">
              {{ $t('inviteMemberTitle') }}
            </f7-button>
        </f7-block>
      </f7-page-content>
    </AdaptiveSheet>
  </f7-page>
</template>

<script setup>
import { ref, computed } from "vue";
import { f7 } from "framework7-vue";
import MemberCard from "../components/group/MemberCard.vue";
import RoleChangeSheet from "../components/group/RoleChangeSheet.vue";
import AdaptiveSheet from "../components/common/AdaptiveSheet.vue";
import PendingInvitationRow from "../components/group/PendingInvitationRow.vue";
import { useMembersStore } from "../js/pinia-stores/members.js";
import { useKeycloakStore } from "../js/pinia-stores/keycloak.js";
import { useGroupsStore } from '../js/pinia-stores/group.js'

import i18n from "../js/i18n.js";
const { t } = i18n.global;

const props = defineProps({
  groupId: { type: String, required: true },
  groupName: { type: String, default: "" },
});

const membersStore = useMembersStore();
const keycloakStore = useKeycloakStore();
const groupStore=useGroupsStore();

const selectedMember = ref(null);
const roleChangeSheetRef = ref(null);
const inviteSheetOpen = ref(false);
const inviteTarget = ref("");
const channel=ref("WHATSAPP");
const overdueUserIds = ref(new Set());
const searchQuery = ref("");
const activeFilter = ref("all");

const group=computed(()=>groupStore.activeGroup);

function logoUrl(grp) {
  const g = grp || group.value;
  return g?.logoUrl || g?.logo || "";
}

function initials(name) {
  if (!name) return "G";
  return String(name).trim().slice(0, 2).toUpperCase();
}

function statusLabel(grp) {
  const g = grp || group.value;
  if (!g?.status || g.status === "ACTIVE") return "";
  const key = `groups.status.${g.status}`;
  const label = t(key);
  return label !== key ? label : g.status;
}

const groupStatus = computed(() => statusLabel(group.value));
const groupStatusClass = computed(() => {
  const s = (group.value?.status || '').toLowerCase();
  return s ? `status-${s}` : '';
});

const admins = computed(() =>
  membersStore.members.filter((m) => m.role === "OWNER" || m.role === "ADMIN"),
);
const regularMembers = computed(() =>
  membersStore.members.filter((m) => m.role === "MEMBER"),
);
const guests = computed(() =>
  membersStore.members.filter((m) => m.role === "GUEST"),
);

const filteredMembers = computed(() => {
  return membersStore.members.filter((m) => {
    const matchesSearch = !searchQuery.value || (m.name || "").toLowerCase().includes(searchQuery.value.toLowerCase());
    if (activeFilter.value === "admins") return matchesSearch && (m.role === "OWNER" || m.role === "ADMIN");
    if (activeFilter.value === "regulars") return matchesSearch && m.role === "MEMBER";
    return matchesSearch;
  });
});

const filteredAdmins = computed(() =>
  filteredMembers.value.filter((m) => m.role === "OWNER" || m.role === "ADMIN"),
);
const filteredRegulars = computed(() =>
  filteredMembers.value.filter((m) => m.role === "MEMBER"),
);

const pendingCount = computed(
  () => membersStore.sentInvitations.length + membersStore.joinRequests.length,
);

const viewerCanManage = computed(() => {
  const viewer = membersStore.members.find(
    (m) => m.userId === keycloakStore.subject,
  );
  return viewer?.role === "OWNER" || viewer?.role === "ADMIN";
});

async function load() {
  await Promise.all([
    membersStore.fetchMembers(props.groupId),
    membersStore.fetchInvitations(props.groupId),
  ]);
}

function openMenu(member, targetEl) {
  selectedMember.value = member;
  roleChangeSheetRef.value?.openRoleChangeActionsPopover(member, targetEl);
}

async function handleRoleChange(newRole) {
  try {
    await membersStore.changeRole(
      props.groupId,
      selectedMember.value.userId,
      newRole,
    );
    f7.toast.show({ text: t("roleUpdated"), closeTimeout: 1500 });
  } catch (e) {
    f7.dialog.alert(
      e?.response?.data?.message || t("roleUpdateFailed"),
    );
  }
}

async function handleRemove() {
  f7.dialog.confirm(
    t("memberRemoveConfirm", { memberName: selectedMember.value.name }),
    async () => {
      await membersStore.removeMember(
        props.groupId,
        selectedMember.value.userId,
      );
      f7.toast.show({ text: t("memberRemoved"), closeTimeout: 1500 });
    },
  );
}

async function handleNudge(member) {
  await membersStore.nudge(props.groupId, member.userId);
  f7.toast.show({
    text: t("reminderSent", { memberName: member.name }),
    closeTimeout: 1500,
  });
}

async function handleAccept(inv) {
  await membersStore.acceptJoinRequest(props.groupId, inv.id);
  f7.toast.show({ text: t("memberAdded"), closeTimeout: 1500 });
}

async function handleDecline(inv) {
  await membersStore.declineJoinRequest(props.groupId, inv.id);
}

async function handleResend(inv) {
  await membersStore.resendInvitation(props.groupId, inv.id);
  f7.toast.show({ text: t("invitationResent"), closeTimeout: 1500 });
}
function selectInviteChannnel(ch) {
  channel.value = ch;
}

async function sendInvite() {
  if (!inviteTarget.value.trim()) return;
  await membersStore.inviteMember(props.groupId, {
    phoneOrEmail: inviteTarget.value,
    channel,
  });
  inviteTarget.value = "";
  inviteSheetOpen.value = false;
  f7.toast.show({ text: t("invitationSent"), closeTimeout: 1500 });
}

function goImport() {
  f7.views.main.router.navigate(`/groups/${props.groupId}/members/import`);
}
</script>

<style scoped>
/* ── Page shell ─────────────────────────────────────────────────── */
.members-page {
  font-family: 'Work Sans', sans-serif;
  padding: 16px !important;
  max-width: 36rem !important;
  margin-left: auto !important;
  margin-right: auto !important;
}
.members-page > * + * {
  margin-top: 16px !important;
}

/* ── Group summary card ─────────────────────────────────────────── */
.summary-card {
  background-color: var(--f7-card-bg-color, #ffffff) !important;
  border-radius: 1rem !important;
  padding: 16px !important;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06) !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 12px !important;
  position: relative !important;
}
.summary-head {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  gap: 12px !important;
}
.summary-head-main {
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
}
.summary-titles {
  min-width: 0 !important;
  padding-right: 60px !important;
}
.summary-logo {
  width: 40px !important;
  height: 40px !important;
  border-radius: 12px !important;
  object-fit: cover !important;
  flex-shrink: 0 !important;
  border: 2px solid #fff !important;
  background: var(--f7-card-bg-color, #fff) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12) !important;
}
.summary-icon {
  width: 40px !important;
  height: 40px !important;
  border-radius: 12px !important;
  background-color: rgba(26, 107, 66, 0.1) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  color: #1A6B42 !important;
  flex-shrink: 0 !important;
  border: 2px solid #fff !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12) !important;
}
.summary-name {
  margin: 0 !important;
  font-family: 'Manrope', sans-serif;
  font-size: 20px;
  font-weight: 700 !important;
  line-height: 28px;
  color: var(--f7-on-surface, #161d18) !important;
  display: -webkit-box !important;
  -webkit-line-clamp: 1 !important;
  -webkit-box-orient: vertical !important;
  overflow: hidden !important;
}
.summary-subtitle {
  margin: 0 !important;
  font-family: 'Work Sans', sans-serif;
  font-size: 11px;
  font-weight: 500 !important;
  line-height: 14px;
  color: #3f4941 !important;
}
.summary-status,
.summary-status-pill {
  position: absolute !important;
  top: 8px !important;
  right: 8px !important;
  display: inline-flex !important;
  align-items: center !important;
  padding: 3px 8px !important;
  border-radius: 9999px !important;
  font-family: 'Work Sans', sans-serif;
  font-size: 9px !important;
  font-weight: 700 !important;
  line-height: 14px;
  text-transform: uppercase !important;
  letter-spacing: 0.03em !important;
  flex-shrink: 0 !important;
  backdrop-filter: blur(4px) !important;
}
.summary-status {
  background-color: #fed977 !important;
  color: #2d2411 !important;
}
.summary-status-pill.status-draft,
.summary-status-pill.status-archived {
  background: rgba(142, 142, 147, 0.85) !important;
  color: #fff !important;
}
.summary-status-pill.status-suspended {
  background: rgba(119, 46, 55, 0.9) !important;
  color: #fff !important;
}
.summary-stats {
  display: grid !important;
  grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  gap: 8px !important;
  padding-top: 4px !important;
}
.summary-stat {
background-color: var(--f7-surface-container-low, #edf6ed) !important;
  border-radius: 0.75rem !important;
  padding: 10px !important;
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
}
.stat-icon {
  width: 28px !important;
  height: 28px !important;
  border-radius: 9999px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
}
.stat-icon--primary {
  background-color: #1A6B42 !important;
  color: #ffffff !important;
}
.stat-icon--secondary {
  background-color: #ffe08f !important;
  color: #2d2411 !important;
}
.stat-body {
  min-width: 0 !important;
}
.stat-value {
  margin: 0 !important;
  font-family: 'Work Sans', sans-serif;
  font-size: 14px;
  font-weight: 700 !important;
  line-height: 20px;
color: var(--f7-on-surface, #161d18) !important;
overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

/* ── Primary action buttons ─────────────────────────────────────── */
.invite-btn {
  background-color: #1a6b42 !important;
  color: #ffffff !important;
  border: none !important;
  width: 100% !important;
  padding: 14px 16px !important;
  gap: 8px !important;
  font-family: 'Work Sans', sans-serif !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  line-height: 20px !important;
  text-transform: none !important;
  transition: all 0.2s ease;
}
.invite-btn--shadow-sm {
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06) !important;
}
.invite-btn:active {
  transform: scale(0.98);
}

/* ── Search & filter chips ──────────────────────────────────────── */
.filters > * + * {
  margin-top: 8px !important;
}
.search {
  position: relative !important;
  width: 100% !important;
}
.search-icon {
  position: absolute !important;
  top: 0 !important;
  bottom: 0 !important;
  left: 12px !important;
  display: flex !important;
  align-items: center !important;
  color: var(--f7-on-surface-variant, #6f7a71) !important;
}
.avocash-input {
  width: 100%;
  padding: 10px 14px;
  background-color: var(--f7-surface-container-low, #edf6ed);
  border: 1px solid var(--f7-list-item-border-color, #bfc9bf);
  border-radius: 10px;
  font-size: 15px;
  color: var(--f7-on-surface, #161d18);
  outline: none;
}
.avocash-input:focus {
  border-color: var(--f7-theme-color, #1a6b42);
}
.search-input {
  padding-left: 40px !important;
  padding-right: 16px !important;
}
.filter-chips {
  display: flex !important;
  align-items: center !important;
  gap: 6px !important;
  overflow-x: auto !important;
  padding-top: 4px !important;
  padding-bottom: 4px !important;
  -ms-overflow-style: none !important;
  scrollbar-width: none !important;
}
.filter-chips::-webkit-scrollbar {
  display: none !important;
}
.filter-chip {
  flex-shrink: 0 !important;
  padding: 6px 12px !important;
  border-radius: 9999px !important;
  font-family: 'Work Sans', sans-serif !important;
  font-size: 11px !important;
  font-weight: 500 !important;
  line-height: 14px !important;
  display: flex !important;
  align-items: center !important;
  gap: 4px !important;
  transition: all 0.15s ease !important;
}
.filter-chip--all {
  font-weight: 600 !important;
}
.filter-chip--active {
  background-color: #1A6B42 !important;
  color: #ffffff !important;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05), 0 0 0 1px rgba(16, 24, 40, 0.04) !important;
}
.filter-chip--idle {
  background-color: var(--f7-card-bg-color, #ffffff) !important;
  color: #3f4941 !important;
}
.filter-chip-count {
  background-color: rgba(255, 255, 255, 0.2) !important;
  padding: 1px 6px !important;
}

/* ── Tab panels ─────────────────────────────────────────────────── */
.tab-panel {
  padding-top: 8px !important;
}
.tab-panel > * + * {
  margin-top: 16px !important;
}
.section-title {
  margin: 0 0 8px !important;
  font-family: 'Manrope', sans-serif;
  font-size: 14px;
  font-weight: 700 !important;
  line-height: 20px;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  color: var(--f7-on-surface-variant, #6f7a71) !important;
}
.member-group > * + * {
  margin-top: 8px !important;
}
.tab-footer {
  padding-top: 8px !important;
}
.import-btn-label {
  font-family: 'Work Sans', sans-serif;
  font-size: 12px;
  font-weight: 600 !important;
  line-height: 16px;
}
.empty-invites {
  background-color: var(--f7-card-bg-color, #ffffff) !important;
  padding: 24px !important;
  border-radius: 1rem !important;
  text-align: center !important;
  font-family: 'Work Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: var(--f7-on-surface-variant, #6f7a71) !important;
}


</style>
